import { useCallback, useRef, useState } from "react";

type Item = { id: number; visible: boolean };

/** The ids of the hidden tracks or waypoints, shared by `useUserTracks` and `useUserWaypoints` */
export const useHiddenIds = () => {
  const [hiddenIds, setHiddenIdsState] = useState<number[]>([]);
  // Read by `toggle`, which mustn't save from inside a state updater (React may call one twice)
  const hiddenIdsRef = useRef(hiddenIds);

  const setHiddenIds = useCallback((update: (ids: number[]) => number[]) => {
    hiddenIdsRef.current = update(hiddenIdsRef.current);
    setHiddenIdsState(hiddenIdsRef.current);
  }, []);

  /** The hidden ones of these items, e.g. after they were loaded */
  const setHiddenFrom = useCallback(
    (items: Item[] | null) =>
      setHiddenIds(
        () =>
          items?.filter((item) => item.visible === false).map(({ id }) => id) ??
          []
      ),
    [setHiddenIds]
  );

  const setHidden = useCallback(
    (ids: number[], hidden: boolean) =>
      setHiddenIds((current) => {
        if (!hidden) {
          const remaining = current.filter((id) => !ids.includes(id));
          return remaining.length < current.length ? remaining : current;
        }
        const added = ids.filter((id) => !current.includes(id));
        return added.length > 0 ? [...current, ...added] : current;
      }),
    [setHiddenIds]
  );

  /**
   * Hides or shows the item right away and saves that with `saveVisibility`. When saving throws, the change is
   * rolled back and the error rethrown.
   */
  const toggle = useCallback(
    async (
      id: number,
      saveVisibility: (id: number, visible: boolean) => Promise<unknown>
    ) => {
      const visible = hiddenIdsRef.current.includes(id);
      setHidden([id], !visible);
      try {
        await saveVisibility(id, visible);
      } catch (error) {
        setHidden([id], visible);
        throw error;
      }
    },
    [setHidden]
  );

  return { hiddenIds, setHiddenFrom, setHidden, toggle };
};
